import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Checkbox } from "@/shared/ui/checkbox";
import { Form } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Loader2, Plus, RefreshCw, Trash2, Pencil } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Field } from "./AppFormSections";
import { KfAvatar } from "./KfAvatar";
import {
  wecomAppService,
  type KfAccount,
  type KfAccountRequest,
  type RemoteKfAccount,
} from "@/entities/wecom-app";

type Props = {
  tenantId: number;
  appId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** 客服账号管理:从微信同步(批量勾选 bind/unbind)+ 手动增删改。自包含,容器只传 appId。 */
export function KfBindingSheet({ tenantId, appId, open, onOpenChange }: Props) {
  const { t } = useTranslation("wecom");
  const confirm = useConfirm();

  const kfSchema = z.object({
    openKfid: z.string().trim().min(1, t("appList.openKfidRequired")),
    kfName: z.string(),
  });
  type KfValues = z.infer<typeof kfSchema>;

  const [editingKf, setEditingKf] = useState<KfAccount | null>(null);
  const [kfFormOpen, setKfFormOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [remoteAccounts, setRemoteAccounts] = useState<RemoteKfAccount[]>([]);
  const [selectedKfids, setSelectedKfids] = useState<Set<string>>(new Set());

  const kfForm = useForm<KfValues>({
    resolver: zodResolver(kfSchema),
    mode: "onBlur",
    defaultValues: { openKfid: "", kfName: "" },
  });

  const { data: kfAccounts = [] } = useQuery({
    queryKey: ["wecom-kf-accounts", tenantId, appId],
    queryFn: () => wecomAppService.listKfAccounts(tenantId, appId!),
    enabled: open && !!appId,
  });

  const createKfMutation = useMutationWithToast({
    mutationFn: (values: KfValues) => {
      const payload: KfAccountRequest = values;
      return editingKf
        ? wecomAppService.updateKfAccount(tenantId, appId!, editingKf.id, payload)
        : wecomAppService.createKfAccount(tenantId, appId!, payload);
    },
    successToast: () => ({
      title: editingKf ? t("appList.kfUpdateSuccess") : t("appList.kfCreateSuccess"),
    }),
    errorToast: t("appList.operationFailed"),
    invalidateKeys: [["wecom-kf-accounts", tenantId, appId]],
    onSuccess: () => {
      setKfFormOpen(false);
      setEditingKf(null);
    },
  });

  const deleteKfMutation = useMutationWithToast({
    mutationFn: (kfId: number) => wecomAppService.deleteKfAccount(tenantId, appId!, kfId),
    errorToast: t("appList.operationFailed"),
    invalidateKeys: [["wecom-kf-accounts", tenantId, appId]],
  });

  const fetchRemoteMutation = useMutationWithToast({
    mutationFn: () => wecomAppService.listRemoteKfAccounts(tenantId, appId!),
    errorToast: t("appList.fetchRemoteFailed"),
    onSuccess: (data) => {
      setRemoteAccounts(data);
      setSelectedKfids(new Set(data.filter((a) => a.bound).map((a) => a.openKfid)));
      setSyncOpen(true);
    },
  });

  const syncConfirmMutation = useMutationWithToast({
    mutationFn: async () => {
      const toBind = remoteAccounts.filter((a) => !a.bound && selectedKfids.has(a.openKfid));
      const toUnbind = remoteAccounts.filter((a) => a.bound && !selectedKfids.has(a.openKfid));
      let added = 0;
      let removed = 0;
      if (toBind.length > 0) {
        added = await wecomAppService.bindKfAccounts(
          tenantId,
          appId!,
          toBind.map((a) => ({ openKfid: a.openKfid, name: a.name, avatar: a.avatar }))
        );
      }
      if (toUnbind.length > 0) {
        removed = await wecomAppService.unbindKfAccounts(
          tenantId,
          appId!,
          toUnbind.map((a) => a.openKfid)
        );
      }
      return { added, removed };
    },
    successToast: ({ added, removed }) => {
      const parts: string[] = [];
      if (added > 0) parts.push(t("appList.syncBound", { count: added }));
      if (removed > 0) parts.push(t("appList.syncUnbound", { count: removed }));
      return {
        title: t("appList.syncSuccess"),
        description: parts.join("，") || t("appList.syncNoChange"),
      };
    },
    errorToast: t("appList.syncFailed"),
    invalidateKeys: [["wecom-kf-accounts", tenantId, appId]],
    onSuccess: () => setSyncOpen(false),
  });

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t("appCard.kfTitle")}</SheetTitle>
          </SheetHeader>
          <div className="space-y-3 mt-6">
            <div className="flex gap-1 justify-end">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-2xs"
                disabled={fetchRemoteMutation.isPending}
                onClick={() => fetchRemoteMutation.mutate()}
              >
                <RefreshCw
                  className={cn("h-3 w-3 mr-1", fetchRemoteMutation.isPending && "animate-spin")}
                />
                {t("appCard.syncFromWecom")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-2xs"
                onClick={() => {
                  setEditingKf(null);
                  kfForm.reset({ openKfid: "", kfName: "" });
                  setKfFormOpen(true);
                }}
              >
                <Plus className="h-3 w-3 mr-1" />
                {t("appCard.addManually")}
              </Button>
            </div>
            {kfAccounts.length === 0 ? (
              <div className="rounded-md border border-dashed py-6 text-center text-xs text-muted-foreground">
                {t("appCard.kfEmptyState")}
              </div>
            ) : (
              <div className="space-y-1.5">
                {kfAccounts.map((kf) => (
                  <div
                    key={kf.id}
                    className="group flex items-center gap-3 rounded-md border bg-background px-3 py-2 hover:bg-accent/30 transition-colors"
                  >
                    <KfAvatar name={kf.kfName || kf.openKfid} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-medium truncate">
                          {kf.kfName || t("appCard.unnamed")}
                        </span>
                        <span className="text-2xs text-muted-foreground/70 font-mono truncate">
                          {kf.openKfid}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        title={t("appCard.edit")}
                        aria-label={t("appCard.edit")}
                        onClick={() => {
                          setEditingKf(kf);
                          kfForm.reset({ openKfid: kf.openKfid, kfName: kf.kfName ?? "" });
                          setKfFormOpen(true);
                        }}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        title={t("appCard.delete")}
                        aria-label={t("appCard.delete")}
                        disabled={deleteKfMutation.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "解绑此客服账号?",
                            description: "解绑后该客服账号的消息将不再进入 AI 客服。",
                            confirmText: t("appCard.delete"),
                            variant: "destructive",
                          });
                          if (ok) deleteKfMutation.mutate(kf.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* 同步 sheet */}
      <Sheet open={syncOpen} onOpenChange={setSyncOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{t("appList.syncSheetTitle")}</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            {remoteAccounts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                {t("appList.remoteEmpty")}
              </p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">{t("appList.syncCheckHint")}</p>
                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                  {remoteAccounts.map((acc) => {
                    const checked = selectedKfids.has(acc.openKfid);
                    return (
                      <label
                        key={acc.openKfid}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors",
                          checked
                            ? "border-primary/40 bg-primary/5"
                            : "border-border hover:bg-accent/50"
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(val) => {
                            setSelectedKfids((prev) => {
                              const next = new Set(prev);
                              if (val) next.add(acc.openKfid);
                              else next.delete(acc.openKfid);
                              return next;
                            });
                          }}
                        />
                        <KfAvatar name={acc.name} avatar={acc.avatar} size={36} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{acc.name}</div>
                          <div className="text-2xs text-muted-foreground font-mono truncate">
                            {acc.openKfid}
                          </div>
                        </div>
                        {acc.bound && (
                          <Badge
                            variant={checked ? "secondary" : "destructive"}
                            className="text-2xs shrink-0"
                          >
                            {checked ? t("appList.alreadyBound") : t("appList.willUnbind")}
                          </Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
                <Button
                  className="w-full"
                  disabled={syncConfirmMutation.isPending}
                  onClick={() => syncConfirmMutation.mutate()}
                >
                  {syncConfirmMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("appList.syncing")}
                    </>
                  ) : (
                    t("appList.confirmSync")
                  )}
                </Button>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* 手动增改 sheet */}
      <Sheet open={kfFormOpen} onOpenChange={setKfFormOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>
              {editingKf ? t("appList.editKfTitle") : t("appList.newKfTitle")}
            </SheetTitle>
          </SheetHeader>
          <Form {...kfForm}>
            <form
              className="space-y-4 mt-6"
              onSubmit={kfForm.handleSubmit((values) => createKfMutation.mutate(values))}
            >
              <Field label={t("appList.fieldOpenKfid")} required>
                <Input
                  placeholder={t("appList.openKfidPlaceholder")}
                  {...kfForm.register("openKfid")}
                />
                {kfForm.formState.errors.openKfid && (
                  <p className="text-2xs text-destructive">
                    {kfForm.formState.errors.openKfid.message}
                  </p>
                )}
              </Field>
              <Field label={t("appList.fieldKfName")} hint={t("appList.kfNameHint")}>
                <Input
                  placeholder={t("appList.kfNamePlaceholder")}
                  {...kfForm.register("kfName")}
                />
              </Field>
              <Button type="submit" className="w-full" disabled={createKfMutation.isPending}>
                {createKfMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("appList.saving")}
                  </>
                ) : (
                  t("appList.confirmSave")
                )}
              </Button>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </>
  );
}
